export default {
  meta: {
    title: "Wuzhi - CMS, software & digital services for modern teams",
    description:
      "Wuzhi CMS, custom software, cloud infrastructure, and digital transformation. Trusted by organizations worldwide.",
    keywords:
      "Wuzhi CMS, content management system, high-performance website system",
  },
  nav: {
    logoAlt: "Wuzhi",
    products: "Products",
    services: "Services",
    doc: "Docs",
    about: "About us",
    contact: "Contact",
  },
  hero: {
    badge: "Trusted by 100+ organizations nationwide",
    title: "Wuzhi CMS",
    subtitle: "Your all-in-one content management platform",
    value1: "Smart",
    value2: "Reliable",
    value3: "Secure",
    ctaPrimary: "Start your journey",
    ctaSecondary: "View demo",
  },
  features: {
    badge: "Why Wuzhi",
    title: "Built for modern enterprises",
    description:
      "We combine cutting-edge technology with industry best practices to deliver real outcomes for your business.",
    items: [
      {
        title: "High-performance architecture",
        description:
          "End-to-end performance tuning, low latency and high throughput—smooth operations with instant feedback.",
      },
      {
        title: "Enterprise-grade security",
        description:
          "Meets major compliance requirements with access control and audit trails to protect your critical data.",
      },
      {
        title: "Elastic scale",
        description:
          "Modular, domain-driven design for independent scale and releases; grow horizontally and handle surges with ease.",
      },
      {
        title: "24/7 support",
        description:
          "A dedicated support team for round-the-clock technical assistance.",
      },
      {
        title: "Continuous delivery",
        description:
          "Regular feature updates and security patches to keep you ahead of the curve.",
      },
      {
        title: "Intelligent operations",
        description:
          "AI-assisted decisions, proactive alerts, and smart ops to cut costs and improve stability.",
      },
    ],
  },
  about: {
    badge: "About Wuzhi",
    title: "Driving digital excellence since 2010",
    paragraph1:
      "We are a passionate product and engineering team of innovators and problem-solvers, helping organizations transform with modern software. We bridge the gap between technology and business goals—delivering measurable results, growth, and a lasting edge.",
    paragraph2:
      "With deep expertise in cloud, AI, custom software, and digital transformation, we work with companies worldwide to build scalable, secure, and innovative systems that stand the test of time.",
    stats: [
      { value: "500+", label: "Global customers" },
      { value: "1000+", label: "Projects delivered" },
      { value: "15+", label: "Years in the industry" },
      { value: "98%", label: "Client satisfaction" },
    ],
    cards: [
      {
        title: "Innovation first",
        description: "Forward-looking solutions that push boundaries",
      },
      {
        title: "Expert team",
        description: "Certified professionals with deep experience",
      },
      {
        title: "Security by design",
        description: "Enterprise-grade safeguards in every deliverable",
      },
      {
        title: "Proven impact",
        description: "Measurable lift for your business growth",
      },
    ],
  },
  services: {
    badge: "What we do",
    title: "End-to-end technology for your business",
    description:
      "From idea to production, we help you thrive in a digital-first world with full-lifecycle support.",
    cta: "Learn more",
    list: [
      {
        title: "Planning & design",
        description:
          "From discovery to solution architecture—solid foundations for your next build.",
        features: [
          "Requirements analysis",
          "Architecture",
          "Prototype validation",
        ],
      },
      {
        title: "Custom development",
        description:
          "Modern stacks, tailored to your goals—enabling a confident digital shift.",
        features: [
          "Agile delivery",
          "Scalable architecture",
          "Quality assurance",
        ],
      },
      {
        title: "AI & machine learning",
        description:
          "Smart automation and predictive insight powered by advanced models—unlock new opportunities.",
        features: [
          "Data & analytics",
          "Neural networks",
          "Process automation",
        ],
      },
    ],
  },
  testimonial: {
    badge: "Testimonials",
    title: "Trusted by industry leaders",
    description:
      "Hear from customers who have partnered with Wuzhi in the real world.",
    list: [
      {
        name: "Mira Chen",
        role: "CTO, FinanceFlow",
        image: "media/en/testimonials/1.svg",
        content:
          "Wuzhi helped us modernize our legacy stack on the cloud. Their AI integration expertise gave us a clear market advantage.",
      },
      {
        name: "James Luo",
        role: "CEO, RetailHub Global",
        image: "media/en/testimonials/2.svg",
        content:
          "The commerce platform they built handles millions of orders flawlessly. The attention to detail and drive for quality are outstanding.",
      },
      {
        name: "Emily Watson",
        role: "VP of Operations, HealthTech",
        image: "media/en/testimonials/3.svg",
        content:
          "Working with Wuzhi changed how we ship software. The HIPAA-aware platform they delivered exceeded our expectations on both features and security.",
      },
      {
        name: "Weiguo Zhang",
        role: "Director of Digital, SmartLogix",
        image: "media/en/testimonials/4.svg",
        content:
          "Wuzhi delivered at incredible speed—requirements to go-live in six weeks—with stability and UX at a world-class level.",
      },
      {
        name: "Xiaoxia Lin",
        role: "Product Director, EduCloud",
        image: "media/en/testimonials/5.svg",
        content:
          "Our learning platform now serves ten times the concurrent users. They truly understood the domain and made the whole engagement seamless.",
      },
    ],
  },
  cta: {
    badge: "Get in touch",
    title: "Ready to start your digital transformation?",
    phoneValue: "+1 (555) 123-4567",
    emailValue: "contact@techvision.com",
    wechatQrSrc: "",
    addressValue: "123 Tech Boulevard, San Francisco, CA 94105",
    lead:
      "Tell us about your goals—our team is ready to turn your vision into a product that ships and scales.",
    phoneLabel: "Phone",
    emailLabel: "Email",
    officeLabel: "Office",
    qrCodeAlt: "WeChat QR code",
    formName: "Name",
    formNamePlaceholder: "Jane Doe",
    formEmail: "Email address",
    formEmailPlaceholder: "jane@company.com",
    formCompany: "Company",
    formCompanyPlaceholder: "Your company name",
    formMessage: "Message",
    formMessagePlaceholder: "Tell us about your project or requirements...",
    submit: "Send message",
    submitting: "Sending...",
    success: "Message sent. We will get back to you shortly.",
    error: "Something went wrong. Please try again.",
    charCount: "{{count}}/500",
  },
  footer: {
    brand: "Wuzhi",
    tagline: "We empower businesses with technology and digital transformation.",
    localFileLabel: "Localized brand note (sample TXT)",
    localFileUrl: "media/en/files/readme.txt",
    colSolutions: "Solutions",
    colCompany: "Company",
    colNewsletter: "Newsletter",
    solutions: [
      "Custom development",
      "Cloud migration",
      "AI integration",
      "DevOps",
    ],
    companyAbout: "About us",
    companyCareers: "Careers",
    companyCase: "Case studies",
    companyContact: "Contact",
    newsletterLead: "Insights and trends, straight to your inbox.",
    emailPlaceholder: "Your email",
    subscribe: "Subscribe",
    subscribing: "Submitting...",
    subscribeSuccess: "You are subscribed.",
    subscribeError: "Something went wrong. Please try again.",
    copyright: "© {{year}} Wuzhi. All rights reserved.",
  },
  notFound: {
    title: "Page not found",
    subtitle: "This page is not available.",
    hint: "Tell us what you would like to see here.",
  },
};
